<script setup lang="ts">
/**
 * KeyInspectorOverlay
 *
 * Mounted once, globally, in AppLayout.vue. Renders three pieces, all driven
 * by the shared `useKeyInspector()` state:
 *   - a small floating, draggable pill while inspect mode is on, with an
 *     Exit button — deliberately not a full-width banner (the first version
 *     of this tool used one): a banner would sit on top of the header and
 *     user menu, which this version needs reachable, since the app keeps
 *     working normally while inspecting. Starts bottom-center rather than
 *     top-center: it sits above dialogs and toasts (see "Stacking" below),
 *     so at the top it would cover dialog titles and the top-center toasts
 *     an admin is trying to inspect. Draggable because that default spot
 *     can still land on top of content an admin needs to see. Exit keeps the
 *     original ghost-button look (text, contrast severity) rather than a
 *     solid fill, but is given an embossed edge so it still reads as a
 *     control sitting on top of the pill rather than flat with it, and on
 *     hover inverts to a solid white pill with amber text — mirroring the
 *     amber-on-white of the pill itself, flipped.
 *   - an outline + small icon over whatever's currently hovered, once
 *     `useKeyInspector`'s hover walk finds a marker — clicking the icon
 *     (`inspect()`) is what actually opens the popover; hovering alone never
 *     does, so moving the mouse around the page while inspecting has no side
 *     effects
 *   - a small popover anchored at the click position once a key is found
 *     (via that icon, or an Alt+click — see useKeyInspector's docblock)
 *
 * The popover shows the key, its raw template and current text, whether the
 * hospital has customised it (a badge inline with the "Template" label),
 * and lets an admin edit the override in place. Editing happens directly in
 * the Template box: the pencil button next to its label (toggleTemplateEdit())
 * turns the box into a textarea and itself into a checkmark; clicking the
 * checkmark saves, Escape (inside the textarea) discards the draft without
 * saving. There's
 * no separate Edit/Save/Cancel button row — the Template box is the only
 * edit surface. Once a key is customised, a second button appears next to
 * the pencil — restoreDefault() — that clears the override entirely via
 * `DELETE /override/:key`, which this feature re-enables (it was dead code
 * from before Cognito auth existed, never wired to any UI, until now).
 *
 * Stacking: all four pieces sit at z-10000–z-10003 (outline < icon < pill
 * < popover — the popover on top so a bottom-center pill never covers one
 * opened on text near the bottom of the screen), above every PrimeVue layer — modal dialogs, their masks
 * and toasts are stacked from 1100 upward, and the app's own fixed dropdowns
 * use 9999. Anything lower renders underneath an open dialog or toast, where
 * the hover walk still finds its marked text but the outline and icon can't
 * be seen or clicked.
 */
import { ref, computed, watch, onMounted, onUnmounted } from 'vue';
import { useTranslation } from 'i18next-vue';
import Button from 'primevue/button';
import Textarea from 'primevue/textarea';
import Skeleton from 'primevue/skeleton';
import { Icon, useToaster } from '@tms-phoenix/ui-vue';
import { useKeyInspector, KEY_INSPECTOR_IGNORE_ATTR } from '@/shared/composables/useKeyInspector';
import { useSettingsStore } from '@/stores';
import { fetchLocaleKeys, type LocaleKey } from '@/core/services';
import { getApiErrorMessage } from '@/core/utils/api-error.util';

const { t } = useTranslation();
const toast = useToaster();

const { isActive, selected, hovered, exit, clearSelection, inspect } = useKeyInspector();
const settingsStore = useSettingsStore();

// ── Pill — draggable position ─────────────────────────────────────────────────
//
// `pillPosition` stays null until the first drag — the pill's own `bottom-4
// left-1/2 -translate-x-1/2` Tailwind classes (applied only while it's
// null, see template) handle the default bottom-center spot, so an admin who
// never drags it sees no change from before. Deliberately not persisted
// anywhere: inspect mode itself doesn't survive a reload either (see
// useKeyInspector), so there's nowhere natural to restore a remembered
// position from without inventing state that would outlive the thing it
// positions.

const pillEl = ref<HTMLElement>();
const pillPosition = ref<{ x: number; y: number } | null>(null);

/** Margin kept from the viewport edge — matches the hover icon's own edge clamp. */
const PILL_EDGE_MARGIN = 8;

/**
 * Marks the pill's own interactive children (currently just Exit) that must
 * not start a drag on pointerdown — checked instead of guessing by tag name
 * (e.g. `closest('button')`), since that would also need to hold up against
 * however a given child component actually renders its root element.
 */
const PILL_DRAG_EXEMPT_ATTR = 'data-pill-drag-exempt';

/** Null while not dragging. Tracked so a second pointer (e.g. a stray touch) can't hijack an in-progress drag. */
let dragPointerId: number | null = null;
/** Offset from the pointer to the pill's top-left corner at drag start, so the pill doesn't jump to re-center under the cursor. */
let dragOffset = { x: 0, y: 0 };

function clampPillPosition(x: number, y: number): { x: number; y: number } {
  const el = pillEl.value;
  const maxX = Math.max(PILL_EDGE_MARGIN, window.innerWidth - (el?.offsetWidth ?? 0) - PILL_EDGE_MARGIN);
  const maxY = Math.max(PILL_EDGE_MARGIN, window.innerHeight - (el?.offsetHeight ?? 0) - PILL_EDGE_MARGIN);
  return {
    x: Math.min(Math.max(PILL_EDGE_MARGIN, x), maxX),
    y: Math.min(Math.max(PILL_EDGE_MARGIN, y), maxY),
  };
}

function startPillDrag(event: PointerEvent): void {
  // Only the pill's own surface starts a drag — not the Exit button, which
  // needs its own click to keep firing normally rather than being absorbed
  // into a (zero-distance) drag.
  if ((event.target as Element).closest(`[${PILL_DRAG_EXEMPT_ATTR}]`)) return;
  const el = pillEl.value;
  if (!el) return;

  event.preventDefault();
  const rect = el.getBoundingClientRect();
  dragPointerId = event.pointerId;
  dragOffset = { x: event.clientX - rect.left, y: event.clientY - rect.top };
  // Fix the current on-screen position before dropping the bottom-center
  // default anchor, so the pill doesn't jump on the very first drag.
  pillPosition.value = clampPillPosition(rect.left, rect.top);
  // Pointer capture keeps move/up events targeted at the pill even once the
  // cursor outruns it during a fast drag — no document-level listeners needed.
  el.setPointerCapture(event.pointerId);
}

function dragPill(event: PointerEvent): void {
  if (dragPointerId === null || event.pointerId !== dragPointerId) return;
  pillPosition.value = clampPillPosition(event.clientX - dragOffset.x, event.clientY - dragOffset.y);
}

function endPillDrag(event: PointerEvent): void {
  if (dragPointerId === null || event.pointerId !== dragPointerId) return;
  dragPointerId = null;
}

// Re-clamp on resize (e.g. devtools opening, orientation change) so a
// previously-dragged pill can't end up stranded off-screen.
function handlePillViewportResize(): void {
  if (!pillPosition.value) return;
  pillPosition.value = clampPillPosition(pillPosition.value.x, pillPosition.value.y);
}

onMounted(() => window.addEventListener('resize', handlePillViewportResize));
onUnmounted(() => window.removeEventListener('resize', handlePillViewportResize));

const pillStyle = computed(() => {
  if (!pillPosition.value) return {};
  return { left: `${pillPosition.value.x}px`, top: `${pillPosition.value.y}px` };
});

// ── Hover outline / icon ──────────────────────────────────────────────────────
//
// Hidden once a popover is open — with the popover already pinned to this
// same spot, redrawing the outline/icon underneath it would just be visual
// clutter, and the pointer has likely moved to the popover's own controls
// anyway (which carry KEY_INSPECTOR_IGNORE_ATTR, so hovering them doesn't
// disturb `hovered` — see useKeyInspector.ts).
const showHoverIcon = computed(() => hovered.value !== null && selected.value === null);

/** Matches the icon's own h-6/w-6 box (see template) so it sits just outside the outline's corner rather than overlapping the text underneath it. */
const HOVER_ICON_SIZE = 24;

const hoverOutlineStyle = computed(() => {
  const rect = hovered.value?.rect;
  if (!rect) return {};
  return {
    left: `${rect.left}px`,
    top: `${rect.top}px`,
    width: `${rect.width}px`,
    height: `${rect.height}px`,
  };
});

const hoverIconStyle = computed(() => {
  const rect = hovered.value?.rect;
  if (!rect) return {};
  // Anchored to the outline's top-right corner, clamped to the viewport so
  // text flush against the right edge (common in RTL-agnostic tables) still
  // gets a clickable icon rather than one rendered off-screen.
  const left = Math.min(rect.right - HOVER_ICON_SIZE / 2, window.innerWidth - HOVER_ICON_SIZE - 4);
  const top = Math.max(rect.top - HOVER_ICON_SIZE / 2, 4);
  return { left: `${left}px`, top: `${top}px` };
});

// ── Status lookup for the selected key ───────────────────────────────────────
//
// Deliberately calls fetchLocaleKeys() directly rather than going through
// settingsStore.loadLocaleKeys() — that mutates the store's shared
// localeKeys/localeKeysQuery state, which would clobber the Localization
// page's own search results if it happens to be open at the same time as
// this (global, page-independent) overlay.

const isLoadingStatus = ref(false);
const keyRow = ref<LocaleKey | null>(null);
const statusLookupFailed = ref(false);

const isEditing = ref(false);
const editValue = ref('');
const isSaving = ref(false);
const isRestoring = ref(false);
const validationError = ref('');

async function refreshKeyStatus(key: string): Promise<void> {
  isLoadingStatus.value = true;
  statusLookupFailed.value = false;
  try {
    // search does a substring ILIKE match, not an exact lookup (see
    // locale.repo.ts's findKeys) — filtering by exact key below is what
    // actually pins down the right row.
    const result = await fetchLocaleKeys({ search: key, limit: '10' });
    keyRow.value = result.data.find((row) => row.key === key) ?? null;
  } catch {
    statusLookupFailed.value = true;
    keyRow.value = null;
  } finally {
    isLoadingStatus.value = false;
  }
}

watch(
  () => selected.value?.key,
  (key) => {
    isEditing.value = false;
    validationError.value = '';
    keyRow.value = null;
    if (key) {
      void refreshKeyStatus(key);
    }
  },
);

/** The key exists in `locale_strings` — i.e. the bundle has been synced at least once since this key was added. */
const existsInDb = computed(() => keyRow.value !== null);
const isOverridden = computed(() => keyRow.value?.is_customized === true);

/**
 * The raw (pre-interpolation) template shown in the "Template" box and used
 * to validate edits. Prefers the database's resolved_value/custom_value —
 * the same authoritative source {@link startEdit} seeds its draft from —
 * over `selected.template`, which comes from the *browser's own* cached
 * i18next bundle (see useKeyInspector's `lookupTemplate`). That bundle can
 * lag behind the database: saving an override through this very popover
 * updates the DB and reloads the bundle, but any other recent sync (another
 * admin's save, a `/locale/sync` deploy) only reaches this browser on its
 * next full reload. Falling back to `selected.template`/`matchedText` only
 * matters once the key isn't in the database at all (`existsInDb` false),
 * where there's nothing fresher to prefer.
 */
const displayTemplate = computed(
  () => keyRow.value?.resolved_value ?? keyRow.value?.custom_value ?? selected.value?.template ?? selected.value?.matchedText,
);

/**
 * True once the draft matches what editing started from (startEdit() seeds
 * editValue from this same displayTemplate). Keeps the checkmark disabled
 * for an unmodified draft, rather than letting a no-op save go through —
 * always false while not editing, since there's nothing to compare yet.
 */
const isUnchanged = computed(() => isEditing.value && editValue.value === displayTemplate.value);

// ── Placeholder preservation ──────────────────────────────────────────────────

const PLACEHOLDER_RE = /\{\{[^}]+\}\}/g;

const templatePlaceholders = computed(() => {
  const template = displayTemplate.value ?? '';
  return Array.from(template.matchAll(PLACEHOLDER_RE)).map((m) => m[0]);
});

const hasPlaceholders = computed(() => templatePlaceholders.value.length > 0);

/**
 * Built here rather than as `$t(...)` in the template — same reason as
 * HospitalConfigView's `mrnLabelHint`/`patientLabelHint`: the string
 * contains a literal `{{placeholders}}`, which Vue's template compiler
 * would otherwise treat as the start of a nested interpolation and fail to
 * parse the surrounding mustache.
 */
const placeholderWarning = computed(() =>
  t('keyInspector.placeholderWarning', {
    defaultValue: 'Contains {{placeholders}} — keep them so tenant-configured terms still reach this text.',
    placeholders: templatePlaceholders.value.join(', '),
  }),
);

function missingPlaceholders(value: string): string[] {
  return templatePlaceholders.value.filter((p) => !value.includes(p));
}

// ── Edit / save ────────────────────────────────────────────────────────────────

function startEdit(): void {
  if (!selected.value?.key) return;
  editValue.value = displayTemplate.value ?? '';
  validationError.value = '';
  isEditing.value = true;
}

function cancelEdit(): void {
  isEditing.value = false;
  validationError.value = '';
}

async function save(): Promise<void> {
  const key = selected.value?.key;
  if (!key) return;

  const missing = missingPlaceholders(editValue.value);
  if (missing.length > 0) {
    validationError.value = t('keyInspector.missingPlaceholders', {
      defaultValue: 'This would remove {{placeholders}} from the text. Keep it so this term stays tenant-configurable.',
      placeholders: missing.join(', '),
    });
    return;
  }

  isSaving.value = true;
  try {
    // The store action persists the override (and logs the audit event),
    // then applies the response's already-fresh bundle directly to the live
    // i18next instance — see applyFreshBundle's docblock — which is what
    // makes every other mounted $t()/t() binding for this key update
    // immediately. Deliberately not also calling
    // localeStore.reloadAfterOverrideChange() here: that re-fetches the
    // bundle over HTTP, which can be served from a cache up to 5 minutes
    // stale and silently revert the save that was just applied.
    await settingsStore.saveLocaleOverrides({ [key]: editValue.value });
    toast.success(t('settings.localization.rowSavedSuccess', { translationKey: key }), {
      detail: t('settings.localization.visibleAfterReload'),
    });
    isEditing.value = false;
    await refreshKeyStatus(key);
  } catch (err) {
    toast.danger(
      t('keyInspector.saveFailed', {
        defaultValue: 'Couldn’t save the translation for {{translationKey}}. Your edit is still open — please try again.',
        translationKey: key,
      }),
      { detail: getApiErrorMessage(err, '') || undefined },
    );
  } finally {
    isSaving.value = false;
  }
}

/**
 * The single pencil/checkmark button next to the "Template" label: starts
 * editing in place, or — once already editing — saves. Replaces the earlier
 * separate Edit button + bottom Save/Cancel pair with one inline toggle, so
 * the Template box itself becomes the edit surface rather than a second
 * textarea appearing further down the popover.
 */
function toggleTemplateEdit(): void {
  if (isEditing.value) {
    void save();
  } else {
    startEdit();
  }
}

/**
 * Restore default — reverts this hospital's override entirely via
 * DELETE /override/:key (settingsStore.resetLocaleOverride), rather than
 * saving the default text as a new override: a true revert clears
 * custom_value outright, so a future change to the base/translated text
 * (another /locale/sync) reaches this tenant again, which saving the
 * default text as an override would not — that would just pin today's
 * default text in place as if an admin had deliberately chosen it.
 */
async function restoreDefault(): Promise<void> {
  const key = selected.value?.key;
  if (!key) return;

  isRestoring.value = true;
  try {
    await settingsStore.resetLocaleOverride(key);
    toast.success(
      t('keyInspector.restoredSuccess', { defaultValue: '{{translationKey}} restored to the default translation', translationKey: key }),
      { detail: t('settings.localization.visibleAfterReload') },
    );
    await refreshKeyStatus(key);
  } catch (err) {
    toast.danger(
      t('keyInspector.restoreFailed', {
        defaultValue: 'Couldn’t restore {{translationKey}} to the default translation. Please try again.',
        translationKey: key,
      }),
      { detail: getApiErrorMessage(err, '') || undefined },
    );
  } finally {
    isRestoring.value = false;
  }
}

// ── Other actions ────────────────────────────────────────────────────────────

async function copyKey(): Promise<void> {
  const key = selected.value?.key;
  if (!key) return;
  try {
    await navigator.clipboard.writeText(key);
    toast.success(t('keyInspector.copiedKey', { defaultValue: 'Key copied' }));
  } catch {
    // Clipboard access can be denied (permissions, insecure context) —
    // non-fatal, the key is still visible to copy by hand.
  }
}

// ── Popover position ─────────────────────────────────────────────────────────

/** Matches the popover's own max-w-sm (24rem / 384px) plus a small margin. */
const POPOVER_WIDTH = 384;

const popoverStyle = computed(() => {
  const pos = selected.value?.position;
  if (!pos) return {};
  const left = Math.min(Math.max(8, pos.x), window.innerWidth - POPOVER_WIDTH - 8);
  const top = Math.min(pos.y + 12, window.innerHeight - 120);
  return { left: `${left}px`, top: `${Math.max(8, top)}px` };
});
</script>

<template>
  <div
    v-if="isActive"
    ref="pillEl"
    class="fixed z-10002 flex cursor-move items-center gap-3 rounded-full bg-amber-500 px-4 py-2 text-sm font-medium text-white shadow-lg select-none"
    :class="pillPosition ? '' : 'bottom-4 left-1/2 -translate-x-1/2'"
    :style="pillStyle"
    :title="$t('keyInspector.dragHint', 'Drag to move')"
    data-testid="key-inspector-pill"
    :[KEY_INSPECTOR_IGNORE_ATTR]="true"
    @pointerdown="startPillDrag"
    @pointermove="dragPill"
    @pointerup="endPillDrag"
    @pointercancel="endPillDrag"
  >
    <Icon
      name="Translate"
      size-class="h-4 w-4"
      color="white"
      :decorative="true"
    />
    <span>
      {{ $t('keyInspector.pillText', 'Inspecting translations — hover text, click its icon. Exports and printing are disabled.') }}
    </span>
    <Button
      :label="$t('keyInspector.exit', 'Exit')"
      severity="contrast"
      size="small"
      text
      class="shrink-0 rounded-full bg-white/10! px-3 font-semibold text-white! shadow-[0_1px_2px_rgba(0,0,0,0.15)] transition-colors duration-150 hover:bg-white! hover:text-amber-600! hover:shadow-sm!"
      :[PILL_DRAG_EXEMPT_ATTR]="true"
      data-testid="key-inspector-exit"
      @click="exit"
    />
  </div>

  <!-- Hover outline + icon — see showHoverIcon's docblock for why both are
       hidden once a popover is open. pointer-events-none on the outline lets
       mouseover pass through to whatever's actually underneath it. -->
  <div
    v-if="showHoverIcon"
    class="pointer-events-none fixed z-10000 rounded border-2 border-amber-500/70"
    :style="hoverOutlineStyle"
    data-testid="key-inspector-hover-outline"
  />
  <button
    v-if="showHoverIcon"
    type="button"
    class="fixed z-10001 flex h-6 w-6 items-center justify-center rounded-full bg-amber-500 text-white shadow-md hover:bg-amber-600"
    :style="hoverIconStyle"
    :aria-label="$t('keyInspector.inspectIcon', 'Inspect translation key')"
    data-testid="key-inspector-hover-icon"
    :[KEY_INSPECTOR_IGNORE_ATTR]="true"
    @click="inspect"
  >
    <Icon
      name="Translate"
      size-class="h-3.5 w-3.5"
      color="white"
      :decorative="true"
    />
  </button>

  <div
    v-if="selected"
    class="fixed z-10003 w-full max-w-sm rounded-lg border border-gray-200 bg-white p-4 shadow-xl"
    :style="popoverStyle"
    data-testid="key-inspector-popover"
    :[KEY_INSPECTOR_IGNORE_ATTR]="true"
  >
    <div class="flex items-center justify-between gap-2">
      <div class="min-w-0 flex-1">
        <p
          v-if="selected.key"
          class="flex items-center gap-1.5 font-mono text-xs font-semibold text-gray-900 break-all"
        >
          {{ selected.key }}
          <button
            type="button"
            class="shrink-0 text-gray-400 hover:text-gray-600"
            :aria-label="$t('keyInspector.copyKey', 'Copy key')"
            :title="$t('keyInspector.copyKey', 'Copy key')"
            data-testid="key-inspector-copy"
            @click="copyKey"
          >
            <Icon
              name="FileCopy"
              size-class="h-3.5 w-3.5"
              variant="stroke"
              :decorative="true"
            />
          </button>
        </p>
        <p
          v-else
          class="text-xs font-semibold text-gray-500"
        >
          {{ $t('keyInspector.notTranslatable', 'Not translatable — no matching key found') }}
        </p>
      </div>
      <button
        type="button"
        class="shrink-0 text-gray-400 hover:text-gray-600"
        :aria-label="$t('common.close', 'Close')"
        :title="$t('common.close', 'Close')"
        data-testid="key-inspector-close"
        @click="clearSelection"
      >
        <Icon
          name="Close"
          size-class="h-4 w-4"
          :decorative="true"
        />
      </button>
    </div>

    <template v-if="selected.key">
      <!-- Full-content skeleton while the override-status lookup is in
           flight — stands in for everything below the header (template,
           current text, status badge/note, action buttons) at once, rather
           than showing that content immediately and having the status
           badge pop in late once the request resolves. Sized to roughly
           match the real layout rather than any one specific outcome.
           role="status" + aria-label carries the same announcement the old
           plain-text loading state gave screen readers; the pulsing bars
           alone would say nothing to assistive tech. -->
      <div
        v-if="isLoadingStatus"
        class="mt-3 space-y-3"
        role="status"
        :aria-label="$t('keyInspector.loadingStatus', 'Checking override status…')"
        data-testid="key-inspector-content-skeleton"
      >
        <div class="space-y-1.5">
          <Skeleton
            width="4rem"
            height="0.65rem"
          />
          <Skeleton
            width="100%"
            height="1.5rem"
          />
        </div>
        <div class="space-y-1.5">
          <Skeleton
            width="5.5rem"
            height="0.65rem"
          />
          <Skeleton
            width="90%"
            height="1rem"
          />
        </div>
        <Skeleton
          width="7rem"
          height="1.1rem"
          border-radius="9999px"
        />
        <div class="flex justify-end gap-2">
          <Skeleton
            width="6rem"
            height="2rem"
            border-radius="9999px"
          />
          <Skeleton
            width="4rem"
            height="2rem"
            border-radius="9999px"
          />
        </div>
      </div>

      <template v-else>
        <p
          v-if="selected.resolvedVia === 'fallback'"
          class="mt-2 text-xs text-amber-700"
        >
          {{ $t('keyInspector.fallbackNotice', 'Not marked — matched by comparing text. Double-check before saving.') }}
        </p>

        <div
          v-if="selected.otherMatches.length > 0"
          class="mt-2 text-xs text-gray-500"
        >
          {{ $t('keyInspector.otherMatches', 'Also found in this text:') }}
          <span class="font-mono">{{ selected.otherMatches.map((m) => m.key).join(', ') }}</span>
        </div>

        <div class="mt-3">
          <div class="flex items-center justify-between gap-2">
            <p class="flex min-w-0 items-center gap-1.5 text-xs font-medium text-gray-500">
              {{ $t('keyInspector.template', 'Template') }}
              <span
                v-if="isOverridden"
                class="inline-flex shrink-0 items-center rounded-full bg-blue-50 px-2 py-0.5 text-xs font-medium text-blue-700"
              >
                {{ $t('keyInspector.overridden', 'Customized for this hospital') }}
              </span>
            </p>
            <div class="flex shrink-0 items-center gap-1">
              <!-- Restore default — only offered once there's an override to
                   restore from (isOverridden), and hidden while editing or
                   saving: restoring mid-edit would discard a draft the admin
                   hasn't decided to abandon yet. See restoreDefault(). -->
              <button
                v-if="isOverridden && !isEditing"
                type="button"
                class="shrink-0 text-gray-400 hover:text-gray-600 disabled:cursor-not-allowed disabled:opacity-40 disabled:hover:text-gray-400"
                :disabled="isRestoring"
                :aria-label="$t('keyInspector.restoreDefault', 'Restore default translation')"
                :title="$t('keyInspector.restoreDefault', 'Restore default translation')"
                data-testid="key-inspector-restore-default"
                @click="restoreDefault"
              >
                <Icon
                  :name="isRestoring ? 'RefreshData' : 'ClockArrow'"
                  size-class="h-3.5 w-3.5"
                  :variant="isRestoring ? 'stroke' : 'fill'"
                  :class="{ 'animate-spin': isRestoring }"
                  :decorative="true"
                />
              </button>
              <!-- The one toggle for the whole inline-edit flow: pencil starts
                   it (seeding editValue from displayTemplate), checkmark saves
                   it — see toggleTemplateEdit(). While that save is in flight,
                   the checkmark becomes a spinning icon instead, since
                   clicking it already fired the save and there's nothing left
                   to confirm by clicking it again. Disabled while the key
                   isn't in the database yet (nothing to save against), a
                   save or restore is already in flight (no double-submit;
                   only one of the two actions at a time), or — once editing
                   — the draft is unmodified (isUnchanged; nothing to save). -->
              <button
                type="button"
                class="shrink-0 text-gray-400 hover:text-gray-600 disabled:cursor-not-allowed disabled:opacity-40 disabled:hover:text-gray-400"
                :disabled="!existsInDb || isSaving || isRestoring || isUnchanged"
                :aria-label="isEditing ? $t('common.save', 'Save') : $t('common.edit', 'Edit')"
                :title="isEditing ? $t('common.save', 'Save') : $t('common.edit', 'Edit')"
                data-testid="key-inspector-template-edit-toggle"
                @click="toggleTemplateEdit"
              >
                <Icon
                  :name="isSaving ? 'RefreshData' : isEditing ? 'IconSuccess' : 'PenEdit'"
                  size-class="h-3.5 w-3.5"
                  variant="stroke"
                  :class="{ 'animate-spin': isSaving }"
                  :decorative="true"
                />
              </button>
            </div>
          </div>

          <Textarea
            v-if="isEditing"
            v-model="editValue"
            rows="2"
            class="mt-0.5 w-full rounded bg-gray-50 px-2 py-1 font-mono text-xs text-gray-700"
            data-testid="key-inspector-edit-textarea"
            @keydown.esc.stop="cancelEdit"
          />
          <p
            v-else
            class="mt-0.5 rounded bg-gray-50 px-2 py-1 font-mono text-xs text-gray-700 break-words"
          >
            {{ displayTemplate }}
          </p>

          <p
            v-if="hasPlaceholders"
            class="mt-2 text-xs text-amber-700"
          >
            {{ placeholderWarning }}
          </p>
          <p
            v-if="isEditing && validationError"
            class="mt-1 text-xs text-red-600"
          >
            {{ validationError }}
          </p>
        </div>

        <div class="mt-2">
          <p class="text-xs font-medium text-gray-500">
            {{ $t('keyInspector.currentText', 'Current text') }}
          </p>
          <p class="mt-0.5 text-sm text-gray-900 break-words">
            {{ selected.matchedText }}
          </p>
        </div>

        <div
          v-if="statusLookupFailed"
          class="mt-2 text-xs text-red-600"
        >
          {{ $t('keyInspector.statusLookupFailed', 'Could not check whether this key is customized.') }}
        </div>
        <p
          v-else-if="!existsInDb"
          class="mt-2 text-xs text-gray-500"
        >
          {{ $t('keyInspector.notInDatabase', "This key hasn't been synced yet — editing is disabled until the next deploy.") }}
        </p>
      </template>
    </template>
  </div>
</template>
